/**
 * SPEC-08 §3.2 — `<PageContainer/>`.
 *
 * The single shell every page mounts through, so we never again ship a
 * `mx-auto max-w-6xl` centered column that leaves ~49 % of the data area as
 * dead margin (the live measurement that motivated this SPEC).
 *
 *   • `variant="data"`  — `w-full max-w-[1600px] px-6` (no `mx-auto`
 *     centering until > 1600px; on ultra-wide it centers so lines don't
 *     stretch absurdly). Default for results, lists, tables.
 *   • `variant="reading"` — `mx-auto max-w-3xl` for forms (engagement
 *     creation, contract entry) and prose / explainer-heavy screens.
 *
 * `AppLayout` reduces its own horizontal padding to `px-0`, so this
 * primitive owns the gutters — no double-padding bug.
 */

import { cn } from "@/lib/utils";

export interface PageContainerProps {
  variant?: "data" | "reading";
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
}

export function PageContainer({
  variant = "data",
  className,
  children,
  "data-testid": testId,
}: PageContainerProps) {
  return (
    <div
      data-variant={variant}
      data-testid={testId ?? "page-container"}
      className={cn(
        "w-full",
        variant === "data"
          ? "max-w-[1600px] mx-auto px-6"
          : "max-w-3xl mx-auto px-4",
        className,
      )}
    >
      {children}
    </div>
  );
}
